import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormFeedback, Input, InputProps } from 'reactstrap';
import './textarea.scss'
import { parseCommonError } from '../../../utils/commons';

export type TextareaHandler = {
  validate: (value?: string) => boolean;
}

interface TextareaProps extends InputProps {
  invalidMessage?: string;
  onValueChange?: (value: string) => {};
}

const Textarea = forwardRef<TextareaHandler, TextareaProps>((props: TextareaProps, ref: ForwardedRef<any>) => {
  const { maxLength = 0, value, invalidMessage, onValueChange, onChange, onInvalid, invalid, ...inputProps } = props

  const [textValue, setTextValue] = useState<string>('')
  const [error, setError] = useState<string>('')

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setError(invalidMessage ?? '')
  }, [invalidMessage])

  useEffect(() => {
    const textValue = value ? String(value) : ''
    setTextValue(textValue)
    validateValue(textValue)
  }, [value])

  useImperativeHandle(ref, () => ({
    validate(value?: string): boolean {
      return validateValue(value ? value : textValue)
    }
  }), [textValue])

  const validateValue = (value: string | undefined): boolean => {
    let msg = ''
    if (value && maxLength > 0 && value.length > maxLength) {
      msg = `This value is too long. It should have ${maxLength} characters or fewer.`
    }

    inputRef.current?.setCustomValidity(msg)
    setError(msg)
    return !msg
  }

  const changeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    validateValue(event.target.value)
    setTextValue(event.target.value)
    onValueChange?.(event.target.value)
    onChange?.(event)
  }

  const handleInvalid = (event: React.InvalidEvent<HTMLInputElement>) => {
    event.preventDefault()

    if (validateValue(event.target.value)) {
      const msg = parseCommonError(event)
      setError(msg)
    }

    onInvalid?.(event)
  }

  return (
    <>
      <Input
        type="textarea"
        style={{ width: '100%' }}
        value={textValue}
        onChange={changeValue}
        invalid={invalid || !!error}
        innerRef={inputRef}
        onInvalid={handleInvalid}
        {...inputProps}
      />
      <div className={`text-count ${textValue?.length > maxLength ? 'text-danger' : ''}`}>
        {textValue?.length?.toLocaleString() ?? 0}/{maxLength?.toLocaleString()} Characters
      </div>
      <FormFeedback>{error || invalidMessage}</FormFeedback>
    </>
  )
})

export default Textarea
